/**
 * The one place that talks to the API.
 *
 * Session state lives in an HttpOnly cookie the browser cannot read, so there is no token handling
 * here at all. Two consequences worth knowing: every request must send credentials, and every
 * mutating request must carry the CSRF header the API insists on.
 */

/** Matches the header the API's CSRF middleware requires on any non-GET /api request. */
const CSRF_HEADER = 'X-Requested-With';
const CSRF_VALUE = 'OnBrandMail';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** RFC 7807 body when the API sent one. */
    readonly problem?: ProblemDetails,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The caller is not signed in. The shell turns this into the sign-in screen rather than an error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Either the tenant does not exist or the caller has no role covering it — the API deliberately
   * does not distinguish, so neither does the UI.
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set(CSRF_HEADER, CSRF_VALUE);

    // FormData sets its own Content-Type, and it has to: the header carries the multipart boundary
    // that the browser generates. Declaring JSON over it produces a body the server cannot parse.
    if (init.body !== undefined && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(path, {
    ...init,
    headers,
    // Same-origin in both dev (via the Vite proxy) and production (API serves the SPA).
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new ApiError(response.status, await describeFailure(response), await readProblem(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('problem+json') && !contentType.includes('application/json')) {
    return undefined;
  }

  try {
    return (await response.clone().json()) as ProblemDetails;
  } catch {
    return undefined;
  }
}

async function describeFailure(response: Response): Promise<string> {
  const problem = await readProblem(response);
  if (problem?.detail || problem?.title) {
    return problem.detail ?? problem.title ?? `${response.status} ${response.statusText}`;
  }

  // Some endpoints return a plain `{ message }` body (e.g. Stripe Connect Conflict) rather than
  // RFC 7807 problem+json. Prefer that over a bare "409 Conflict".
  try {
    const body = (await response.clone().json()) as { message?: unknown };
    if (typeof body.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }
  } catch {
    // not JSON
  }

  return `${response.status} ${response.statusText}`;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Multipart POST, for the one thing the portal sends that is not JSON: an uploaded file. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};
